import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  serverTimestamp,
  collection,
  getDocs,
  query,
  orderBy
} from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Check if a user is an admin
 * @param {string} uid - The user ID to check
 * @returns {Promise<boolean>} - Whether the user is an admin
 */
export const checkIsAdmin = async (uid) => {
  try {
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return userSnap.data().isAdmin === true;
    }
    
    return false;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

/**
 * Create or update a user document in Firestore
 * @param {Object} user - The user object from Firebase Auth
 * @param {boolean} isAdmin - Whether the user should be an admin
 * @returns {Promise<void>}
 */
export const createUserDocument = async (user, isAdmin = false) => {
  if (!user) return;
  
  const userRef = doc(db, 'users', user.uid);
  const userSnap = await getDoc(userRef);
  
  if (!userSnap.exists()) {
    // Create new user document
    try {
      await setDoc(userRef, {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || '',
        photoURL: user.photoURL || '',
        isAdmin: isAdmin,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error creating user document:', error);
    }
  } else {
    // Update existing user document
    try {
      await updateDoc(userRef, {
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating user document:', error);
    }
  }
};

/**
 * Set a user as an admin
 * @param {string} uid - The user ID to make an admin
 * @returns {Promise<boolean>} - Whether the operation was successful
 */
export const setUserAsAdmin = async (uid) => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      isAdmin: true,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error setting user as admin:', error);
    return false;
  }
};

/**
 * Remove admin status from a user
 * @param {string} uid - The user ID to remove admin status from
 * @returns {Promise<boolean>} - Whether the operation was successful
 */
export const removeAdminStatus = async (uid) => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      isAdmin: false,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error removing admin status:', error);
    return false;
  }
};

/**
 * Get all users from Firestore
 * @returns {Promise<Array>} - Array of user objects
 */
export const getAllUsers = async () => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return users;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};